import {
  Button,
  Checkbox,
  FormControl,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import React, {useCallback, useEffect, useState} from 'react';
import '../../App.css';
import FirestoreManager from '../../api/firebaseManager';
import {useAuth} from '../../hooks/use-auth';
import {Modal} from '../modal/modal';

const buttonStyle = {
  zIndex: 1,
  backgroundColor: '#c5c7c6',
  height: '30px',
  border: '0px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0px 32px',
  margin: '16px 32px 0px 32px',
  color: 'black',
};

// Ramp OAuth (authorization_code) config. client_id is public; the secret and
// token exchange stay server-side (ramp-exchangeToken). Sandbox consent host is
// demo.ramp.com. Overridable via env for prod (app.ramp.com) without a rebuild.
const RAMP_AUTHORIZE_URL =
  process.env.REACT_APP_RAMP_OAUTH_URL || 'https://demo.ramp.com/v1/authorize';
const RAMP_CLIENT_ID =
  process.env.REACT_APP_RAMP_CLIENT_ID ||
  'ramp_id_OyLgBymnDhYeiM9xfB16y7aE8nZ9Sg2u4wiuOqH2';
// Scopes a customer consents to. reimbursements:read is needed because
// reimbursements arrive on their own webhook event and are fetched from a
// separate endpoint, and entities:read for the journal accounts (FOR-2192).
// Scopes bind at token issuance, so adding one here means existing connections
// must reconnect to gain it.
const RAMP_SCOPE =
  process.env.REACT_APP_RAMP_SCOPE ||
  'accounting:read accounting:write transactions:read reimbursements:read business:read entities:read';
const RAMP_REDIRECT_URI =
  process.env.REACT_APP_RAMP_REDIRECT_URI ||
  `${window.location.origin}/rampConnect`;

export const RampModal = ({active, setActive}) => {
  const {
    state: {profile},
  } = useAuth();

  // FOR-2187: pick entities from a checkbox table. Until the Forest entity
  // endpoint exists the backend reports `unavailable` and we fall back to the
  // original single-entity text field so the flow keeps working.
  const [entities, setEntities] = useState([]);
  const [entitiesUnavailable, setEntitiesUnavailable] = useState(false);
  const [loadingEntities, setLoadingEntities] = useState(true);
  const [checked, setChecked] = useState({});
  const [manualEntity, setManualEntity] = useState('');
  const [busy, setBusy] = useState(false);
  // Customers can have a lot of entities (TestWPF has 83 active), so the list
  // needs to be filterable and scrollable rather than a wall of checkboxes.
  const [entityFilter, setEntityFilter] = useState('');

  const loadEntities = useCallback(async () => {
    if (!profile?.customerID) return;
    setLoadingEntities(true);
    try {
      const res = await FirestoreManager.listRampEntities({
        customerID: profile.customerID,
      });
      if (res?.unavailable || !res?.success) {
        setEntitiesUnavailable(true);
        setEntities([]);
      } else {
        setEntities(res.entities || []);
        setEntitiesUnavailable(false);
      }
    } catch (err) {
      // Treat any failure as "no list available" and use the manual field.
      setEntitiesUnavailable(true);
      setEntities([]);
    }
    setLoadingEntities(false);
  }, [profile?.customerID]);

  useEffect(() => {
    loadEntities();
  }, [loadEntities]);

  const toggle = name =>
    setChecked(prev => ({...prev, [name]: !prev[name]}));

  const visibleEntities = entityFilter ?
    entities.filter(e =>
      e.name.toLowerCase().includes(entityFilter.trim().toLowerCase()),
    ) :
    entities;

  const selectedEntityNames = entitiesUnavailable ?
    (manualEntity ? [manualEntity] : []) :
    entities.filter(e => checked[e.name]).map(e => e.name);

  const startOAuth = async () => {
    if (!selectedEntityNames.length) {
      window.alert(
        entitiesUnavailable ?
          'Enter the Forest entity name to continue.' :
          'Select at least one Forest entity to connect.',
      );
      return;
    }

    setBusy(true);

    // With the manual field there's no list to validate against, so check that
    // the typed entity resolves to Forest data before leaving for Ramp.
    if (entitiesUnavailable) {
      try {
        const preview = await FirestoreManager.previewRampEntity({
          customerID: profile.customerID,
          entityName: selectedEntityNames[0],
        });
        if (preview?.success && !preview.found) {
          const proceed = window.confirm(
            `No Forest accounts were found for entity ` +
              `"${selectedEntityNames[0]}" under customer ` +
              `"${profile.customerID}". This usually means the entity name ` +
              `doesn't match. Connect anyway?`,
          );
          if (!proceed) {
            setBusy(false);
            return;
          }
        }
      } catch (err) {
        // Non-fatal: if the check itself fails, let the user proceed.
      }
    }

    // Ramp redirects back to /rampConnect with ?code&state. Stash what the
    // callback needs (entities + a CSRF state to verify on return).
    const state = `ramp_${Math.random().toString(36).slice(2)}_${Date.now()}`;
    localStorage.setItem('ramp_oauth_state', state);
    localStorage.setItem(
      'ramp_entity_names',
      JSON.stringify(selectedEntityNames),
    );

    const url =
      `${RAMP_AUTHORIZE_URL}?response_type=code` +
      `&client_id=${encodeURIComponent(RAMP_CLIENT_ID)}` +
      `&redirect_uri=${encodeURIComponent(RAMP_REDIRECT_URI)}` +
      `&scope=${encodeURIComponent(RAMP_SCOPE)}` +
      `&state=${encodeURIComponent(state)}`;

    window.location.href = url;
  };

  return (
    <Modal active={active} setActive={setActive}>
      <div className="box flex-column">
        <div>
          <Typography sx={{textAlign: 'center', fontSize: '22px'}}>
            Connecting to Ramp
          </Typography>
          <Typography>
            Connect your Forest database with Ramp. You'll be sent to Ramp to
            approve access; afterward the Forest entities you select below —
            along with their accounts and payees — are shared with Ramp so
            transactions can be coded against them.
          </Typography>
          <br />
          <Typography>
            Contact Support@forestsystems.com if you have any questions.
          </Typography>

          <div className="box">
            {loadingEntities && (
              <Typography sx={{fontStyle: 'italic'}}>
                Loading Forest entities...
              </Typography>
            )}

            {!loadingEntities && !entitiesUnavailable && (
              <>
                {entities.length > 10 && (
                  <FormControl fullWidth sx={{mb: 1}}>
                    <TextField
                      id="entity_filter"
                      label={`Filter ${entities.length} entities`}
                      value={entityFilter}
                      type="text"
                      size="small"
                      onChange={evt => setEntityFilter(evt.target.value)}
                      variant="filled"
                    />
                  </FormControl>
                )}
                <div style={{maxHeight: '260px', overflowY: 'auto'}}>
                <table style={{width: '100%'}}>
                  <thead>
                    <tr>
                      <th style={{textAlign: 'left'}}>Forest Entity</th>
                      <th style={{textAlign: 'left', width: '100px'}}>
                        Connect
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleEntities.map(entity => (
                      <tr key={entity.id || entity.name}>
                        <td>{entity.name}</td>
                        <td>
                          <Checkbox
                            checked={!!checked[entity.name]}
                            onChange={() => toggle(entity.name)}
                          />
                        </td>
                      </tr>
                    ))}
                    {!visibleEntities.length && (
                      <tr>
                        <td colSpan={2}>
                          {entities.length ?
                            'No entities match that filter.' :
                            `No active Forest entities found for ${profile?.customerID}.`}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
                <br />
                <Typography>
                  {selectedEntityNames.length} selected. Check every Forest
                  entity that should be shared with Ramp.
                  You can change this later from the connection's More Info
                  screen.
                </Typography>
              </>
            )}

            {!loadingEntities && entitiesUnavailable && (
              <>
                <Grid container spacing={0}>
                  <Grid
                    item
                    xs={6}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}>
                    <Typography>Forest Entity Name:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <TextField
                        id="entity_name"
                        label="Entity Name"
                        placeholder="entity_name"
                        value={manualEntity}
                        type="text"
                        onChange={evt => setManualEntity(evt.target.value)}
                        variant="filled"
                      />
                    </FormControl>
                  </Grid>
                </Grid>
                <br />
                <Typography>
                  To view the name of the Forest entity you are trying to
                  connect, log on to Forest View and Users | Administrative.
                  The name is displayed under Select a database.
                </Typography>
              </>
            )}
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}>
          <Button
            disabled={busy}
            onClick={() => setActive(false)}
            style={buttonStyle}>
            Close
          </Button>
          <Button
            disabled={busy || loadingEntities}
            style={buttonStyle}
            onClick={startOAuth}>
            {busy ? 'Checking...' : 'Connect with Ramp'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
